import React, { useCallback, useEffect } from 'react';
import { FORM_TYPE_EDIT_DESTINATION } from 'js/constants/formTypes';
import DatabaseConfigurator from 'js/components/destination/destination-content/database/DatabaseConfigurator';
import SourceDatabaseConfigurator from 'js/components/data_sources/add_ds_form/database/DatabaseConfigurator';
import useDatabase from 'new/components/flows/configure/Database/useDatabase';
import { CONNECTION_CATEGORY_DATABASE } from 'js/constants/connectionCategories';
import flowTypes from 'new/components/flows/types/flowTypes';
import { DataBaseTable } from '@nexla/@types';
import { useHistory } from 'react-router-dom';
import { useForm } from 'new/elements/Form/useForm';
import createDatabaseService from 'new/components/flows/configure/Database/service/createDatabaseService';
import useRequests from 'new/state/request/repository/useRequests';
import useFlowParams from '../../useFlowParams';

interface DatabaseProps {
  nexset?: any;
  sink?: any;
  source: any;
  connectionType: 'SINK' | 'SOURCE';
  selectedConnection: string;
  nexsetSchema?: any;
  selectedBucket: any;
  selectedTable: DataBaseTable;
  formType: string;
  onTableSelected: (table: any, database: any) => void;
  onSinkConfigChanged?: (sinkConfig: any, field: string, value: any) => void;
  onSourceConfigChanged?: (sinkConfig: any, field: string, value: any) => void;
  onSinkMappingUpdated?: (newMapping: any, auto: boolean) => void;
  sinkConfig?: any;
  sourceConfig: any;
  isNewTable?: boolean;
  canCreateTable?: boolean;
  isEditMode: boolean;
  credentialId: number;
  flowType: keyof typeof flowTypes;
  onSaveAdvancedMode(data: any): void;
  nextFormStage(...args: any): any;
}

export default function Database(props: DatabaseProps) {
  const {
    canCreateTable = false,
    selectedConnection,
    sinkConfig,
    sourceConfig,
    nexset,
    source,
    sink,
    selectedBucket,
    selectedTable,
    isNewTable,
    onSinkConfigChanged,
    onTableSelected,
    isEditMode,
    onSinkMappingUpdated,
    onSourceConfigChanged,
    credentialId,
    connectionType,
    formType,
    flowType,
  } = props;
  const {
    org,
    paging,
    data,
    isLoadingSample,
    onRequestProbeTree,
    loadDbDataTypes,
    dbDataTypes,
    schemaToDBDataTypes,
    getProbeData,
    setContextualSupport,
    unsetContextualSupport,
    getDataSchema,
    getNexsetSchemaById,
    sourceConfigFields,
    onRequestInitialDbTree,
    onGetProbeSample,
    updateFormData,
  } = useDatabase(credentialId, selectedConnection, formType, flowType);
  const { showError } = useRequests();
  const history = useHistory();
  const { isModal, onDismiss } = useFlowParams();
  const { addValidation } = useForm();
  const databaseService = createDatabaseService();
  const probe = getProbeData(credentialId);
  const nexsetSchema = getNexsetSchemaById(nexset?.id) || {};
  const dataSchema = getDataSchema({
    connectionCategory: CONNECTION_CATEGORY_DATABASE,
    sinkConfig,
    allAttributes: nexsetSchema,
  });

  // Database validation function - only validate for sinks as sources use different validation
  const validateDatabase = useCallback(() => {
    const tables = probe.flatData || {};
    const sinkConfigFields = paging.sinkConfigById[selectedConnection] || [];

    return databaseService.validateDatabase(
      {
        selectedBucket,
        configFields: connectionType === 'SOURCE' ? sourceConfigFields : sinkConfigFields,
        config: connectionType === 'SOURCE' ? sourceConfig : sinkConfig,
        selectedConnection,
        isNewTable,
        selectedTable,
        tables: data.tableNamesByDatabase,
        connectionType,
      },
      showError,
    );
  }, [
    connectionType,
    probe.flatData,
    paging.sinkConfigById,
    selectedConnection,
    selectedBucket,
    sinkConfig,
    isNewTable,
    selectedTable,
    databaseService,
    showError,
  ]);

  // Register validation with form
  useEffect(() => {
    // Making sure we are in the new Form
    if (typeof addValidation === 'function') {
      addValidation(validateDatabase, 'database');
    }
  }, [validateDatabase, connectionType]);

  const onInputChange = ({ property, value }) => {
    const newConfig = { ...sourceConfig, [property]: value };
    onSourceConfigChanged(newConfig, property, value);
  };

  const goBack = useCallback(() => {
    if (isModal) {
      onDismiss?.({});
      return;
    }
    history.goBack();
  }, [isModal, onDismiss, history]);

  if (connectionType === 'SOURCE') {
    return (
      // @ts-ignore
      <SourceDatabaseConfigurator
        data={source}
        flowType={flowType}
        selectedCredentialId={credentialId}
        selectedConnection={selectedConnection}
        connectionMetas={paging.metaInfoById}
        sourceConfigFields={paging.sourceConfigById}
        visibleFieldData={sourceConfig}
        sourceConfig={sourceConfig}
        sourceBeingEdited={source}
        onSourceConfigChanged={onSourceConfigChanged}
        updateFormData={updateFormData}
        onRequestProbeTree={onRequestProbeTree}
        onRequestInitialDBProbeTree={onRequestInitialDbTree}
        onGetProbeSample={onGetProbeSample(sourceConfig)}
        isLoadingProbeData={probe.loading}
        probeData={probe.data}
        probeDataFlat={probe.flatData}
        probeError={probe.error}
        probeTreeLastUpdatedAt={probe.lastUpdated}
        probeSample={probe}
        isLoadingProbeSample={isLoadingSample}
        selectedDatabase={(selectedBucket || {}).database}
        selectedTable={selectedTable}
        onTableSelected={onTableSelected}
        setContextualSupport={setContextualSupport}
        unsetContextualSupport={unsetContextualSupport}
        sourceName={source?.name || sourceConfig?.name}
        sourceDescription={source?.description || sourceConfig?.description}
        selectedBucket={selectedBucket}
        sourceConfigFieldsList={sourceConfigFields}
        onInputChange={onInputChange}
        onChangeSelectedCredentialDetails={onInputChange}
        selectedConnectionDetails={paging.metaInfoById[selectedConnection]}
        onSaveAdvancedMode={props?.onSaveAdvancedMode}
        nextFormStage={props?.nextFormStage}
        goBack={history.goBack}
        onError={showError}
      />
    );
  }

  return (
    // @ts-ignore
    <DatabaseConfigurator
      data={sink}
      flowType={flowType}
      canCreateTable={canCreateTable}
      addedDataSinkId={sink?.id}
      dataSet={nexset}
      addedDataSink={sink}
      currentOrg={org}
      dataSchema={dataSchema}
      type="configure"
      isEditMode={isEditMode}
      sinkConfigFields={paging.sinkConfigById}
      sinkConfig={sinkConfig}
      onSinkConfigChanged={onSinkConfigChanged}
      selectedDatabase={(selectedBucket || {}).database}
      selectedTable={selectedTable}
      formType={FORM_TYPE_EDIT_DESTINATION}
      onTableSelected={onTableSelected}
      updateFormData={updateFormData}
      selectedCredentialId={credentialId}
      onRequestProbeTree={onRequestProbeTree}
      onRequestInitialDBProbeTree={onRequestInitialDbTree}
      connectionMetas={paging.metaInfoById}
      selectedConnection={selectedConnection}
      isLoadingProbeData={probe.loading}
      probeData={probe.data}
      probeDataFlat={probe.flatData}
      probeError={probe.error}
      isNewTable={isNewTable}
      onSinkMappingUpdated={onSinkMappingUpdated}
      loadDBDataTypes={loadDbDataTypes}
      dbDataTypes={dbDataTypes}
      schemaToDBDataTypes={schemaToDBDataTypes}
      setContextualSupport={setContextualSupport}
      unsetContextualSupport={unsetContextualSupport}
      onSaveAdvancedMode={props?.onSaveAdvancedMode}
      nextFormStage={props?.nextFormStage}
      goBack={history.goBack}
      onError={showError}
    />
  );
}
